import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { BaseController } from '../../base.controller';
import { SecurityController } from '../../security/security.controller';
import { AlertService } from '@shared/src/services/alert/alert.service';
import { LoadingService } from '@shared/src/services/loading/loading.service';
import { RouteCMRDto } from '@shared/src/dtos/route/cmr/RouteCMRDto';
import { RoutePartCMRDto } from '@shared/src/dtos/route/cmr/RoutePartCMRDto';
import { ActionDto } from '@shared/src/dtos/maintenance/action/actionDto';
import { RoutePartCMRAdditionalDataDto } from '@shared/src/dtos/route/cmr/RoutePartCMRAdditionalDataDto';
import { ECMREventDto } from '@shared/src/dtos/route/cmr/ECMREventDto';
import { SaveECMREventDto } from '@shared/src/dtos/route/cmr/SaveECMREventDto';
import { Route_eCMRPartnerDTO } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRPartnerDto';
import { Route_eCMRIncotermDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRIncotermDto';
import { Route_eCMRAddressDTO } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRAddressDto';
import { Route_eCMRPaymentDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRPaymentDto';
import { RoutePartCMRSignatureAgents } from './RoutePartCMRSignatureAgents';
import { RoutePartCMRSignListDto } from './RoutePartCMRSignListDto';
import { UUIDSignatureDto } from './UUIDSignatureDto';
import { Route_eCMRHandlingAndTransportationDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRHandlingAndTransportationDto';
import { Route_eCMRConditionsDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRConditionsDto';
import { Route_eCMRDto } from '@shared/src/dtos/route/cmr/Route_eCMRDto';
import { Route_eCMRPlatesDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRPlatesDto';
import { Route_eCMRDeliveryLinesDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRDeliveryLinesDto';
import { Route_eCMRTransportDateDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRTransportDateDto';
import { RouteDeliveryNotesDto } from '@shared/src/dtos/route/cmr/RouteDeliveryNotesDto';


@Injectable({
    providedIn: 'root',
})
export class RouteCMRController extends BaseController {

    private base = '/routecmr'

    constructor(
        @Inject('SecurityController') protected securityController: SecurityController,
        protected http: HttpClient,
        @Inject('AlertService') protected alertService: AlertService,
        protected translateService: TranslateService,
        @Inject('LoadingService') protected loadingService: LoadingService,
        protected router: Router
    ) {
        super(http, alertService, translateService, loadingService, router);
    }

    public getRouteCMR(routeId: number): Observable<RouteCMRDto> {
        return this.get(this.base + "/" + routeId);
    }

    public getRoutePartCMR(routePartCMRId: number): Observable<RoutePartCMRDto> {
        return this.get(this.base + "/routepartcmr/" + routePartCMRId);
    }

    public getPhaseRoutePartCMR(routePartCMRId: number): Observable<RoutePartCMRDto> {
        return this.get(this.base + "/phaseroutepartcmr/" + routePartCMRId);
    }

    public getRoute_eCMR(routePartCMRId: number): Observable<Route_eCMRDto> {
        return this.get(this.base + "/routeecmr/" + routePartCMRId);
    }

    public getRouteDeliveryNotes(routePartCMRId: number): Observable<RouteDeliveryNotesDto> {
        return this.get(this.base + "/routedeliverynotes/" + routePartCMRId);
    }

    public recalculate(routeId: number): Observable<RouteCMRDto> {
        return this.get(this.base + "/" + routeId + "/recalculate");
    }

    public saveRoutePartCMR(value: RoutePartCMRDto): Observable<RoutePartCMRDto> {
        return this.post(this.base + "/routepartcmr/save", value, "saveRoutePartCMR");
    }

    public getRoutePartCMRAdditionalData(routePartCMRId: number): Observable<RoutePartCMRAdditionalDataDto> {
        return this.get(this.base + "/routepartcmr/" + routePartCMRId + "/additionaldata");
    }
    public saveRoutePartCMRAdditionalData(value: RoutePartCMRAdditionalDataDto): Observable<RoutePartCMRAdditionalDataDto> {
        return this.post(this.base + "/routepartcmr/additionaldata/save", value, "saveRoutePartCMRAdditionalData");
    }

    /** Events de l'eCMR del RoutePartCMR */
    public getECMREvents(routePartCMRId: number): Observable<ECMREventDto[]> {
        return this.get(this.base + "/routepartcmr/" + routePartCMRId + "/events");
    }

    public saveECMREvent(value: SaveECMREventDto): Observable<boolean> {
        return this.post(this.base + "/routepartcmr/events/save", value, "saveECMREvent");
    }

    public updateOriginTenant(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateorigintenant", data, "updateOriginTenant");
    }

    public updateDestinationTenant(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatedestinationtenant", data, "updateDestinationTenant");
    }

    public updateCarrier(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatecarrier", data, "updateCarrier");
    }

    public updateEffectiveCarrier(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateeffectivecarrier", data, "updateEffectiveCarrier");
    }

    public updateDriver(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatedriver", data, "updateDriver");
    }

    public updateApplicant(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateapplicant", data, "updateApplicant");
    }

    public updatePayer(routePartCMRId: number, data: Route_eCMRPartnerDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatepayer", data, "updatePayer");
    }

    public updateIncoterm(routePartCMRId: number, data: Route_eCMRIncotermDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateincoterm", data, "updateIncoterm");
    }

    public updateConditions(routePartCMRId: number, data: Route_eCMRConditionsDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateconditions", data, "updateConditions");
    }

    public updateHandlingAndTransportation(routePartCMRId: number, data: Route_eCMRHandlingAndTransportationDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatehandlingandtransportation", data, "updateHandlingAndTransportation");
    }

    public updatePayment(routePartCMRId: number, data: Route_eCMRPaymentDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatepayment", data, "updatePayment");
    }

    public updateOriginAddress(routePartCMRId: number, data: Route_eCMRAddressDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateoriginaddress", data, "updateOriginAddress");
    }

    public updateDestinationAddress(routePartCMRId: number, data: Route_eCMRAddressDTO): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatedestinationaddress", data, "updateDestinationAddress");
    }

    getHistory(routePartCMRId: number, type: RoutePartCMRSignatureAgents): Observable<RoutePartCMRSignListDto[]> {
        return this.get(this.base + "/routepartcmr/" + routePartCMRId + "/sign/" + type + "/history");
    }
    
    getSignUUIDHistory(singUUID: string, type: RoutePartCMRSignatureAgents): Observable<RoutePartCMRSignListDto[]> {
        return this.get(this.base + "/signUUID/" + singUUID + "/sign/" + type + "/history");
    }
    
    public getSignByAgentAndUUID(agent: any, uuid: string): Observable<UUIDSignatureDto> {
        return this.get(this.base + "/sign/agent/" + agent + "/uuid/" + uuid);
    }

    getRoutePartCMRsByPhaseId(phaseId: number): Observable<RoutePartCMRDto[]> {
    return this.get(this.base + "/byphaseid/" + phaseId);
    }

    public updatePlates(routePartCMRId: number, data: Route_eCMRPlatesDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updateplates", data, "updatePlates");
    }

    public updateDeliveryLines(routePartCMRId: number, data: Route_eCMRDeliveryLinesDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatedeliverylines", data, "updateDeliveryLines");
    }

    public updateTransportDate(routePartCMRId: number, data: Route_eCMRTransportDateDto): Observable<Boolean> {
        return this.put(this.base + "/" + routePartCMRId + "/updatetransportdate", data, "updateTransportDate");
    }
}
